import { Banner, Box } from '@customerio/pluma-components/react';
import { type Collection } from '@/services/db/collection/entity';

type CollectionImportErrorBannerProps = {
  collection: Collection;
  /** Try again + Dismiss both perform edits, so they only render for editors. */
  canEdit: boolean;
  onTryAgain: () => void;
  onDismiss: () => void;
};

/**
 * Presentational only. Retry and dismiss mean different things per surface — the
 * collections index discards a failed first import, the detail page clears the error
 * on a collection the user is still looking at — so both stay with the caller.
 */
export function CollectionImportErrorBanner({
  collection,
  canEdit,
  onTryAgain,
  onDismiss,
}: CollectionImportErrorBannerProps) {
  if ((collection.import_error?.length ?? 0) === 0) {
    return null;
  }

  // "adding" for a collection that never held data (zero rows) vs "updating" for a
  // re-import — based on rows alone, independent of the in-progress import state.
  const description =
    collection.rows === 0
      ? `Retry adding your collection to use data from ${collection.name} in your messages.`
      : `Retry updating your collection to use new data from ${collection.name} in your messages.`;

  return (
    <Box style={{ gridColumn: '1 / -1' }}>
      <Banner
        variant="critical"
        title={`Your ${collection.name} collection couldn't be imported.`}
        description={description}
        // Mirrors the header's Update collection button: same re-import path, same
        // enabled state (editors only, never mid-import).
        action={{ label: 'Try again', onClick: onTryAgain, isDisabled: !canEdit || collection.importing }}
        shouldShowDismissButton={canEdit}
        isDismissButtonDisabled={collection.importing}
        onClose={canEdit ? onDismiss : undefined}
      />
    </Box>
  );
}
